import { Fragment, lazy, Suspense } from 'react';
import MainLayout from '../layouts/MainLayout';
import BlankLayout from '../layouts/BlankLayout';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import { SUPPORTED_LANGUAGES } from '../i18nLanguages';
import { MobileAppProvider } from '../pages/mobile/MobileAppContext';

const Login = lazy(() => import('../pages/admin/login/Login'));
const Dashboard = lazy(() => import('../pages/home/Dashboard'));
const Definitions = lazy(() => import('../pages/definitions/Definitions'));
const CountryList = lazy(() => import('../pages/definitions/country/CountryList'));
const StateList = lazy(() => import('../pages/definitions/state/StateList'));
const CityList = lazy(() => import('../pages/definitions/city/CityList'));
const ConstantsList = lazy(
  () => import('../pages/definitions/constants/ConstantsList')
);
const AllowancesDeductionsList = lazy(
  () =>
    import(
      '../pages/definitions/allowancesDeductions/AllowancesDeductionsList'
    )
);
const LeavesTasksList = lazy(
  () => import('../pages/definitions/leavesTasks/LeavesTasksList')
);
const MonthlyVariables = lazy(
  () => import('../pages/monthlyVariables/MonthlyVariables')
);
const BankAccountList = lazy(
  () => import('../pages/salaries/bankAccount/BankAccountList')
);
const Reports = lazy(() => import('../pages/reports/Reports'));
const MonthlySalariesReport = lazy(
  () => import('../pages/reports/monthlySalaries/MonthlySalariesReport')
);
const TemporaryMonthlySalariesReport = lazy(
  () =>
    import(
      '../pages/reports/temporaryMonthlySalaries/TemporaryMonthlySalariesReport'
    )
);
const SalaryBlockAllDepartmentsReport = lazy(
  () =>
    import(
      '../pages/reports/salaryBlockAllDepartments/SalaryBlockAllDepartmentsReport'
    )
);
const InsuredSalariesReport = lazy(
  () => import('../pages/reports/insuredSalaries/InsuredSalariesReport')
);
const UnionSubscriptionsReport = lazy(
  () =>
    import('../pages/reports/unionSubscriptions/UnionSubscriptionsReport')
);
const EmployeePaidSalariesReport = lazy(
  () =>
    import(
      '../pages/reports/employeePaidSalaries/EmployeePaidSalariesReport'
    )
);
const BankAccountStatementReport = lazy(
  () =>
    import(
      '../pages/reports/bankAccountStatement/BankAccountStatementReport'
    )
);
const TotalEmployeeSalariesReport = lazy(
  () =>
    import(
      '../pages/reports/totalEmployeeSalaries/TotalEmployeeSalariesReport'
    )
);
const EmployeeReport = lazy(
  () => import('../pages/reports/employeeReport/EmployeeReport')
);
const LoansManagement = lazy(
  () => import('../pages/loansManagement/LoansManagement')
);
const EmployeesManagement = lazy(
  () => import('../pages/employeesManagement/EmployeesManagement')
);
const GroupsManagement = lazy(
  () => import('../pages/groupsManagement/GroupsManagement')
);
const LeavesAndTasksList = lazy(
  () => import('../pages/leavesAndTasks/LeavesAndTasksList')
);
const ErrorProcessingList = lazy(
  () => import('../pages/errorProcessing/ErrorProcessingList')
);
const AttendanceRecordsList = lazy(
  () => import('../pages/attendanceRecords/AttendanceRecordsList')
);
const DeviceSettingsList = lazy(
  () => import('../pages/deviceSettings/DeviceSettingsList')
);
const Profile = lazy(() => import('../pages/profile/Profile'));
const ForgotPassword = lazy(
  () => import('../pages/admin/forgotPassword/ForgotPassword')
);
const AcceptInvite = lazy(
  () => import('../pages/admin/acceptInvite/AcceptInvite')
);
const Setup2FaPage = lazy(
  () => import('../pages/admin/setup2faPage/Setup2FaPage')
);
const Verify2FaPage = lazy(
  () => import('../pages/admin/verify2faPage/Verify2FaPage')
);
const MobileLogin = lazy(() => import('../pages/mobile/login/MobileLogin'));
const MobileShell = lazy(() => import('../pages/mobile/MobileShell'));

const MobileProviderOutlet = () => (
  <MobileAppProvider>
    <Outlet />
  </MobileAppProvider>
);

const renderRouteSet = (prefix = '') => {
  return (
    <Fragment key={prefix || 'default'}>
      <Route element={<BlankLayout />}>
        <Route path={`${prefix}/login`} element={<Login />} />
        <Route
          path={`${prefix}/forgot_password`}
          element={<ForgotPassword />}
        />
        <Route
          path={`${prefix}/invite/accept/:accessToken`}
          element={<AcceptInvite />}
        />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <MainLayout fullPage />
          </ProtectedRoute>
        }
      >
        <Route path={`${prefix}/dashboard`} element={<Dashboard />} />
        <Route path={`${prefix}/definitions`} element={<Definitions />} />
        <Route path={`${prefix}/country`} element={<CountryList />} />
        <Route path={`${prefix}/state`} element={<StateList />} />
        <Route path={`${prefix}/city`} element={<CityList />} />
        <Route
          path={`${prefix}/constants/:constantType`}
          element={<ConstantsList />}
        />
        <Route
          path={`${prefix}/allowances_deductions`}
          element={<AllowancesDeductionsList />}
        />
        <Route
          path={`${prefix}/leaves_tasks`}
          element={<LeavesTasksList />}
        />
        <Route
          path={`${prefix}/monthly_variables`}
          element={<MonthlyVariables />}
        />
        <Route
          path={`${prefix}/bank_accounts`}
          element={<BankAccountList />}
        />
        <Route path={`${prefix}/reports`} element={<Reports />} />
        <Route
          path={`${prefix}/report_monthly_salaries`}
          element={<MonthlySalariesReport />}
        />
        <Route
          path={`${prefix}/report_temporary_monthly_salaries`}
          element={<TemporaryMonthlySalariesReport />}
        />
        <Route
          path={`${prefix}/report_salary_block_all_departments`}
          element={<SalaryBlockAllDepartmentsReport />}
        />
        <Route
          path={`${prefix}/report_insured_salaries`}
          element={<InsuredSalariesReport />}
        />
        <Route
          path={`${prefix}/report_union_subscriptions`}
          element={<UnionSubscriptionsReport />}
        />
        <Route
          path={`${prefix}/report_employee_paid_salaries`}
          element={<EmployeePaidSalariesReport />}
        />
        <Route
          path={`${prefix}/report_bank_account_statement`}
          element={<BankAccountStatementReport />}
        />
        <Route
          path={`${prefix}/report_total_employee_salaries`}
          element={<TotalEmployeeSalariesReport />}
        />
        <Route
          path={`${prefix}/report_employee`}
          element={<EmployeeReport />}
        />
        <Route
          path={`${prefix}/loans_management`}
          element={<LoansManagement />}
        />
        <Route
          path={`${prefix}/employees_management`}
          element={<EmployeesManagement />}
        />
        <Route
          path={`${prefix}/groups_management`}
          element={<GroupsManagement />}
        />
        <Route
          path={`${prefix}/leaves_and_tasks`}
          element={<LeavesAndTasksList />}
        />
        <Route
          path={`${prefix}/error_processing`}
          element={<ErrorProcessingList />}
        />
        <Route
          path={`${prefix}/attendance_records`}
          element={<AttendanceRecordsList />}
        />
        <Route
          path={`${prefix}/device_settings`}
          element={<DeviceSettingsList />}
        />
      </Route>
      <Route
        path={`${prefix}/profile`}
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="edit" replace />} />
        <Route path="edit" element={<Profile />} />
        <Route path="set_up_2_step_verification" element={<Setup2FaPage />} />
        <Route
          path="turn_off_2_step_verification"
          element={<Verify2FaPage isDisabled />}
        />
      </Route>
      <Route
        path={`${prefix}/home`}
        element={<Navigate to={`${prefix}/dashboard`} replace />}
      />
      <Route
        path={`${prefix}/*`}
        element={<Navigate to={`${prefix}/login`} replace />}
      />
    </Fragment>
  );
};

const AppRoutes = () => {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Standalone mobile attendance app (mock) */}
        <Route path="/mobile" element={<MobileProviderOutlet />}>
          <Route element={<BlankLayout centered={false} />}>
            <Route index element={<MobileShell />} />
          </Route>
          <Route element={<BlankLayout centered />}>
            <Route path="login" element={<MobileLogin />} />
          </Route>
        </Route>

        {renderRouteSet()}
        {SUPPORTED_LANGUAGES.map((language) => renderRouteSet(`/${language}`))}
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
